import { SessionErrorCode } from './session-errors';
import { SessionOptions, createDefaultSessionOptions } from './session-options';

export enum SessionRunnerStatus {
  IDLE = 'idle',
  DONE = 'done',
  RUNNING = 'running',
}

export type SessionState =
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.RUNNING;
      initializationStatus: SessionRunnerStatus.IDLE;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: SessionErrorCode;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.IDLE;
      initializationStatus: SessionRunnerStatus.IDLE;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: 'done';
      initializationStatus: SessionRunnerStatus.IDLE;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.DONE;
      initializationStatus: SessionRunnerStatus.RUNNING;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: SessionErrorCode;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.DONE;
      initializationStatus: SessionRunnerStatus.IDLE;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.DONE;
      initializationStatus: SessionRunnerStatus.DONE;
      dataPopulationStatus: SessionRunnerStatus.IDLE;
    }
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.DONE;
      initializationStatus: SessionRunnerStatus.DONE;
      dataPopulationStatus: SessionRunnerStatus.RUNNING;
    }
  | {
      error: undefined;
      options: SessionOptions;
      fetchingStatus: SessionRunnerStatus.DONE;
      initializationStatus: SessionRunnerStatus.DONE;
      dataPopulationStatus: SessionRunnerStatus.DONE;
    };

export function selectIsLoading(state: SessionState): boolean {
  return [state.fetchingStatus, state.initializationStatus, state.dataPopulationStatus].some(
    (status) => status === SessionRunnerStatus.RUNNING,
  );
}

export function selectErrorCode(state: SessionState): SessionErrorCode | undefined {
  return state.error;
}

export function selectIsReady(state: SessionState): boolean {
  return [state.fetchingStatus, state.initializationStatus, state.dataPopulationStatus].every(
    (status) => status === SessionRunnerStatus.DONE,
  );
}

export function selectIsInitializedAndFetched(state: SessionState): boolean {
  const error = selectErrorCode(state);
  const isReady = selectIsReady(state);
  const isLoading = selectIsLoading(state);

  if (error) {
    return false;
  }

  if (isLoading) {
    return false;
  }

  if (!isReady) {
    return false;
  }

  // SSR don't want tracking in server. Frontend only.
  if (typeof window === 'undefined') {
    return false;
  }

  const { sessionWidget } = window as any;
  if (!sessionWidget) {
    return false;
  }

  if (typeof sessionWidget.getOutlink !== 'function') {
    return false;
  }

  if (typeof sessionWidget.urlToOutlink !== 'function') {
    return false;
  }

  if (!sessionWidget.session || Object.keys(sessionWidget.session).length === 0) {
    return false;
  }

  return true;
}

export enum SessionActionType {
  SESSION_INSTALLATION_FAILED = '@SESSION/SESSION_INSTALLATION_FAILED',
  SESSION_INSTALLATION_STARTED = '@SESSION/SESSION_INSTALLATION_STARTED',

  SESSION_WIDGET_FETCHING_STARTED = '@SESSION/SESSION_WIDGET_FETCHING_STARTED',
  SESSION_WIDGET_FETCHING_FINISHED = '@SESSION/SESSION_WIDGET_FETCHING_FINISHED',

  SESSION_WIDGET_INITIALIZATION_STARTED = '@SESSION/SESSION_WIDGET_INITIALIZATION_STARTED',
  SESSION_WIDGET_INITIALIZATION_FINISHED = '@SESSION/SESSION_WIDGET_INITIALIZATION_FINISHED',

  SESSION_DATA_POPULATION_STARTED = '@SESSION/SESSION_DATA_POPULATION_STARTED',
  SESSION_DATA_POPULATION_FINISHED = '@SESSION/SESSION_DATA_POPULATION_FINISHED',
}

export interface SessionInstallationFailedAction {
  type: SessionActionType.SESSION_INSTALLATION_FAILED;
  error: SessionErrorCode;
}

export interface SessionInstallationStartedAction {
  type: SessionActionType.SESSION_INSTALLATION_STARTED;
  options: SessionOptions;
}

export interface SessionWidgetFetchingStartedAction {
  type: SessionActionType.SESSION_WIDGET_FETCHING_STARTED;
}

export interface SessionWidgetFetchingSucceededAction {
  type: SessionActionType.SESSION_WIDGET_FETCHING_FINISHED;
}

export interface SessionWidgetInitializationStartedAction {
  type: SessionActionType.SESSION_WIDGET_INITIALIZATION_STARTED;
}

export interface SessionWidgetInitializationSucceededAction {
  type: SessionActionType.SESSION_WIDGET_INITIALIZATION_FINISHED;
}

export interface SessionDataPopulationStartedAction {
  type: SessionActionType.SESSION_DATA_POPULATION_STARTED;
}

export interface SessionDataPopulationFinishedAction {
  type: SessionActionType.SESSION_DATA_POPULATION_FINISHED;
}

export type SessionAction =
  | SessionInstallationFailedAction
  | SessionInstallationStartedAction
  | SessionWidgetFetchingStartedAction
  | SessionWidgetFetchingSucceededAction
  | SessionWidgetInitializationStartedAction
  | SessionWidgetInitializationSucceededAction
  | SessionDataPopulationStartedAction
  | SessionDataPopulationFinishedAction;

export function sessionStateReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case SessionActionType.SESSION_INSTALLATION_STARTED: {
      return {
        ...state,
        options: { ...state.options, ...(action?.options || {}) },
      };
    }

    case SessionActionType.SESSION_WIDGET_FETCHING_STARTED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.RUNNING,
        initializationStatus: SessionRunnerStatus.IDLE,
        dataPopulationStatus: SessionRunnerStatus.IDLE,
      };
    }

    case SessionActionType.SESSION_WIDGET_FETCHING_FINISHED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.DONE,
        initializationStatus: SessionRunnerStatus.IDLE,
        dataPopulationStatus: SessionRunnerStatus.IDLE,
      };
    }

    case SessionActionType.SESSION_WIDGET_INITIALIZATION_STARTED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.DONE,
        initializationStatus: SessionRunnerStatus.RUNNING,
        dataPopulationStatus: SessionRunnerStatus.IDLE,
      };
    }

    case SessionActionType.SESSION_WIDGET_INITIALIZATION_FINISHED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.DONE,
        initializationStatus: SessionRunnerStatus.DONE,
        dataPopulationStatus: SessionRunnerStatus.IDLE,
      };
    }

    case SessionActionType.SESSION_DATA_POPULATION_STARTED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.DONE,
        initializationStatus: SessionRunnerStatus.DONE,
        dataPopulationStatus: SessionRunnerStatus.RUNNING,
      };
    }

    case SessionActionType.SESSION_DATA_POPULATION_FINISHED: {
      return {
        error: undefined,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.DONE,
        initializationStatus: SessionRunnerStatus.DONE,
        dataPopulationStatus: SessionRunnerStatus.DONE,
      };
    }

    case SessionActionType.SESSION_INSTALLATION_FAILED: {
      return {
        error: action.error,
        options: state.options,
        fetchingStatus: SessionRunnerStatus.IDLE,
        initializationStatus: SessionRunnerStatus.IDLE,
        dataPopulationStatus: SessionRunnerStatus.IDLE,
      };
    }

    default:
      return state;
  }
}

export function createDefaultSessionState(): SessionState {
  const options = createDefaultSessionOptions();
  return {
    error: undefined,
    options,
    fetchingStatus: SessionRunnerStatus.RUNNING,
    initializationStatus: SessionRunnerStatus.IDLE,
    dataPopulationStatus: SessionRunnerStatus.IDLE,
  };
}
